import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import {
    getSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    updateSessionCampus,
} from '../../../services/session.js';
import { apiMqtt } from '../../../services/api.js';
import { iAmHigherAuthority } from '../../../libs.jsx';
import {
    fetchShiftHours,
    createShiftDefaults,
    saveShiftHours,
} from './api/shiftSettingsApi.js';

import './shift-settings-page.css';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const SHIFT_NAMES = ['Day', 'Evening', 'Night'];

const SHIFT_COLORS = {
    Day: '#27B4FFFF',
    Evening: '#276BFFFF',
    Night: '#0B16FFFF',
};

/** Short labels for each segment inside the bar. */
const SHIFT_SHORT = { Day: 'Day', Evening: 'Eve', Night: 'Ngt' };

/** Return the next shift in the cycle. */
function nextShift(current) {
    const idx = SHIFT_NAMES.indexOf(current);
    return SHIFT_NAMES[(idx + 1) % SHIFT_NAMES.length];
}

/** Format hour (0-23) into short label like "12a", "1p", etc. */
function shortHourLabel(hour) {
    if (hour === 0) return '12a';
    if (hour === 12) return '12p';
    if (hour < 12) return `${hour}a`;
    return `${hour - 12}p`;
}

/** Format hour (0-23) into "12:00 AM" / "1:00 PM" style. */
function formatHour(hour) {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:00 ${period}`;
}

/**
 * Build a shift summary string from assignments.
 * Groups contiguous hours by shift name and returns ranges.
 * e.g. "Day 7:00 AM – 3:00 PM · Evening 3:00 PM – 10:00 PM · Night 10:00 PM – 7:00 AM"
 */
function buildSummary(assignments) {
    if (!assignments || Object.keys(assignments).length < 24) return '';

    const ranges = [];
    let currentShift = assignments[0]?.shift_name;
    let rangeStart = 0;

    for (let h = 1; h <= 24; h++) {
        const shift = h < 24 ? assignments[h]?.shift_name : null;
        if (shift !== currentShift || h === 24) {
            ranges.push({
                shift: currentShift,
                start: rangeStart,
                end: h === 24 ? 0 : h, // wrap midnight
            });
            currentShift = shift;
            rangeStart = h;
        }
    }

    return ranges
        .map((r) => `${r.shift} ${formatHour(r.start)} – ${formatHour(r.end)}`)
        .join('  ·  ');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const ShiftSettingsPage = () => {
    // -- Session --
    const [campusId, setCampusId] = useState(() => getCampusId());
    const session = useMemo(() => getSession(), []);

    const campusList = useMemo(() => getCampusList() || [], []);

    const campusName = useMemo(() => {
        const match = campusList.find((c) => c.campus_id === campusId);
        return match?.campus_name || `Campus ${campusId}`;
    }, [campusId, campusList]);

    // Permissions
    const editRights = useMemo(() => iAmHigherAuthority(20, session?.role_weight), [session]);
    const adminRights = useMemo(() => iAmHigherAuthority(99, session?.role_weight), [session]);

    // -- Shift data --
    const [shiftData, setShiftData] = useState([]);
    const [assignments, setAssignments] = useState({});
    const [originalAssignments, setOriginalAssignments] = useState({});

    // -- Loading / Saving --
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [restoring, setRestoring] = useState(false);

    // -- Paint brush (drag-to-paint) --
    const [paintBrush, setPaintBrush] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const timelineRef = useRef(null);

    // -- Toast --
    const [toast, setToast] = useState(null);
    const showToast = useCallback((message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    // ======================================================================
    // Data loading
    // ======================================================================
    const loadData = useCallback(async (cId) => {
        setLoading(true);
        try {
            const data = await fetchShiftHours(cId);
            setShiftData(data || []);

            // Build assignments map
            const map = {};
            for (let hour = 0; hour < 24; hour++) {
                map[hour] = { shift_hour_id: null, shift_name: 'Day' };
            }
            if (data && data.length > 0) {
                data.forEach((item) => {
                    if (item.hour_of_day >= 0 && item.hour_of_day < 24) {
                        map[item.hour_of_day] = {
                            shift_hour_id: item.shift_hour_id,
                            shift_name: item.shift_name,
                        };
                    }
                });
            }
            setAssignments(map);
            setOriginalAssignments(JSON.parse(JSON.stringify(map)));
        } catch (err) {
            console.error('[ShiftSettingsPage] Load error:', err);
            showToast('Failed to load shift data.', 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => {
        loadData(campusId);
    }, [campusId, loadData]);

    // ======================================================================
    // Campus switch
    // ======================================================================
    const handleCampusChange = (newId) => {
        setCampusId(newId);
        updateSessionCampus(newId);
        const oldIsCloud = session?.campusData?.[campusId?.toString()]?.is_cloud;
        const newIsCloud = session?.campusData?.[newId?.toString()]?.is_cloud;
        if (oldIsCloud !== newIsCloud) window.location.reload();
    };

    // ======================================================================
    // MQTT Operations
    // ======================================================================
    const handleSyncLocal = async () => {
        setSyncing(true);
        try {
            const [ok] = await apiMqtt({ operation: 'reboot-server', campusId, clientId: getSessionId() });
            showToast(ok ? 'Sync triggered. Please allow up to 3 minutes for changes to take effect.' : 'Error syncing. Contact support if the error persists.', ok ? 'success' : 'error');
        } catch { showToast('Error syncing. Contact support if the error persists.', 'error'); }
        finally { setSyncing(false); }
    };

    const handleRestoreData = async () => {
        setRestoring(true);
        try {
            const [ok] = await apiMqtt({ operation: 'restore-server', campusId, clientId: getSessionId() });
            showToast(ok ? 'Restore triggered. Please allow up to 3 minutes for changes to take effect.' : 'Error restoring. Contact support if the error persists.', ok ? 'success' : 'error');
        } catch { showToast('Error restoring. Contact support if the error persists.', 'error'); }
        finally { setRestoring(false); }
    };

    // ======================================================================
    // Change detection
    // ======================================================================
    const hasChanges = useMemo(() => {
        for (let hour = 0; hour < 24; hour++) {
            const cur = assignments[hour];
            const orig = originalAssignments[hour];
            if (!cur || !orig) return true;
            if (cur.shift_name !== orig.shift_name) return true;
        }
        return false;
    }, [assignments, originalAssignments]);

    // ======================================================================
    // CRUD Handlers
    // ======================================================================
    const handleCreateDefaults = async () => {
        setSaving(true);
        try {
            const [ok] = await createShiftDefaults(campusId, getUserId(), getSessionId());
            if (ok) {
                showToast('Shifts created. All hours default to Day.');
                await loadData(campusId);
            } else {
                showToast('Failed to create shift defaults.', 'error');
            }
        } catch {
            showToast('Failed to create shift defaults.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        const payload = [];
        for (let hour = 0; hour < 24; hour++) {
            const a = assignments[hour];
            payload.push({ shift_hour_id: a.shift_hour_id, shift_name: a.shift_name });
        }
        try {
            const [ok] = await saveShiftHours(payload, {
                campusId,
                userId: getUserId(),
                clientId: getSessionId(),
            });
            if (ok) {
                showToast('Shift assignments saved.');
                setOriginalAssignments(JSON.parse(JSON.stringify(assignments)));
            } else {
                showToast('Failed to save shift assignments.', 'error');
            }
        } catch {
            showToast('Failed to save shift assignments.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setAssignments(JSON.parse(JSON.stringify(originalAssignments)));
    };

    // ======================================================================
    // Timeline interaction — click & drag-to-paint
    // ======================================================================
    const applyShiftToHour = useCallback((hour, shiftName) => {
        setAssignments((prev) => ({
            ...prev,
            [hour]: { ...prev[hour], shift_name: shiftName },
        }));
    }, []);

    const handleSegmentMouseDown = useCallback((hour) => {
        const current = assignments[hour]?.shift_name || 'Day';
        const next = nextShift(current);
        setPaintBrush(next);
        setIsDragging(true);
        applyShiftToHour(hour, next);
    }, [assignments, applyShiftToHour]);

    const handleSegmentMouseEnter = useCallback((hour) => {
        if (isDragging && paintBrush) {
            applyShiftToHour(hour, paintBrush);
        }
    }, [isDragging, paintBrush, applyShiftToHour]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
        setPaintBrush(null);
    }, []);

    // Global mouseup listener to end drag outside the timeline
    useEffect(() => {
        const handleGlobalMouseUp = () => {
            if (isDragging) {
                setIsDragging(false);
                setPaintBrush(null);
            }
        };
        window.addEventListener('mouseup', handleGlobalMouseUp);
        window.addEventListener('touchend', handleGlobalMouseUp);
        return () => {
            window.removeEventListener('mouseup', handleGlobalMouseUp);
            window.removeEventListener('touchend', handleGlobalMouseUp);
        };
    }, [isDragging]);

    // Touch support — resolve hour from touch position
    const getHourFromTouch = useCallback((touch) => {
        if (!timelineRef.current) return null;
        const rect = timelineRef.current.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const segmentWidth = rect.width / 24;
        const hour = Math.floor(x / segmentWidth);
        return hour >= 0 && hour < 24 ? hour : null;
    }, []);

    const handleTouchStart = useCallback((e) => {
        const touch = e.touches[0];
        const hour = getHourFromTouch(touch);
        if (hour !== null) {
            e.preventDefault();
            handleSegmentMouseDown(hour);
        }
    }, [getHourFromTouch, handleSegmentMouseDown]);

    const handleTouchMove = useCallback((e) => {
        if (!isDragging || !paintBrush) return;
        const touch = e.touches[0];
        const hour = getHourFromTouch(touch);
        if (hour !== null) {
            e.preventDefault();
            applyShiftToHour(hour, paintBrush);
        }
    }, [isDragging, paintBrush, getHourFromTouch, applyShiftToHour]);

    // ======================================================================
    // Shift summary
    // ======================================================================
    const summary = useMemo(() => buildSummary(assignments), [assignments]);

    // ======================================================================
    // Render
    // ======================================================================
    const hasData = shiftData.length > 0;

    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ss-page">
                    {/* Toast notification */}
                    {toast ? (
                        <div className={`dp-toast dp-toast--${toast.type}`}>
                            <i className={`fe ${toast.type === 'success' ? 'fe-check-circle' : 'fe-alert-circle'}`} />
                            {toast.message}
                            <button className="dp-toast__close" onClick={() => setToast(null)}>
                                <i className="fe fe-x" />
                            </button>
                        </div>
                    ) : null}

                    {/* Page header */}
                    <div className="dp-page-header">
                        <div className="dp-page-header__inner">
                            <div className="dp-breadcrumb-row">
                                <nav className="dp-breadcrumb">
                                    <span className="dp-breadcrumb__text">Settings</span>
                                    <span className="dp-breadcrumb__separator">/</span>
                                    <span className="dp-breadcrumb__current">Shift Hours</span>
                                </nav>
                                <div className="dp-campus-row">
                                    {editRights ? (
                                        <button className="ap-btn ap-btn--secondary" onClick={handleSyncLocal} disabled={syncing}
                                            title="Syncs local server with cloud data, will cause the server to go offline momentarily during the process">
                                            {syncing ? 'Syncing...' : 'Sync Local'}
                                        </button>
                                    ) : null}
                                    {adminRights ? (
                                        <button className="ap-btn ap-btn--secondary" onClick={handleRestoreData} disabled={restoring}
                                            title="Restore data performs the sync local reboot, however deletes the database file triggering a re-create.">
                                            {restoring ? 'Restoring...' : 'Restore Data'}
                                        </button>
                                    ) : null}
                                    <label htmlFor="sh-campus-selector" className="dp-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select id="sh-campus-selector" value={campusId}
                                            onChange={(e) => handleCampusChange(parseInt(e.target.value, 10))}
                                            className="dp-campus-select">
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="dp-campus-name">{campusName}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="pp-tab-content">
                        {loading ? (
                            <div className="pp-section">
                                <div className="pp-section__body">
                                    <div className="ss-skeleton" style={{ width: '100%', height: '56px', borderRadius: '8px', marginBottom: 'var(--space-3)' }} />
                                    <div className="ss-skeleton" style={{ width: '60%', height: '16px' }} />
                                </div>
                            </div>
                        ) : !hasData ? (
                            /* No data — Setup Shifts prompt */
                            <div className="pp-section">
                                <div className="pp-section__body" style={{ textAlign: 'center', padding: 'var(--space-8, 2rem) var(--space-4, 1rem)' }}>
                                    <p style={{ color: 'var(--color-text-muted, #6b7280)', fontSize: 'var(--font-size-sm, 0.875rem)', marginBottom: 'var(--space-3, 0.75rem)' }}>
                                        No shift configuration exists for this campus. Create the default shift hours to get started.
                                    </p>
                                    <br/>
                                    <button type="button" className="ap-btn ap-btn--primary" onClick={handleCreateDefaults} disabled={saving}>
                                        {saving ? 'Creating...' : 'Setup Shifts'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            /* Timeline bar */
                            <div className="pp-section">
                                <div className="pp-section__body">
                                    {/* Legend */}
                                    <div className="sh-legend">
                                        {SHIFT_NAMES.map((name) => (
                                            <div key={name} className="sh-legend__item">
                                                <span className="sh-legend__swatch" style={{ backgroundColor: SHIFT_COLORS[name] }} />
                                                <span className="sh-legend__label">{name}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Instructions */}
                                    <p className="sh-instructions">
                                        Click an hour to cycle its shift. Click and drag to paint multiple hours.
                                    </p>

                                    {/* Timeline bar */}
                                    <div className="sh-timeline" ref={timelineRef}
                                        onMouseUp={handleMouseUp}
                                        onTouchStart={handleTouchStart}
                                        onTouchMove={handleTouchMove}
                                        onTouchEnd={handleMouseUp}
                                    >
                                        <div className="sh-timeline__bar">
                                            {Array.from({ length: 24 }, (_, hour) => {
                                                const shiftName = assignments[hour]?.shift_name || 'Day';
                                                return (
                                                    <div
                                                        key={hour}
                                                        className="sh-timeline__segment"
                                                        style={{ backgroundColor: SHIFT_COLORS[shiftName] }}
                                                        onMouseDown={(e) => { e.preventDefault(); handleSegmentMouseDown(hour); }}
                                                        onMouseEnter={() => handleSegmentMouseEnter(hour)}
                                                        title={`${formatHour(hour)} — ${shiftName}`}
                                                    >
                                                        <span className="sh-timeline__segment-label">
                                                            {SHIFT_SHORT[shiftName]}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Hour labels */}
                                        <div className="sh-timeline__hours">
                                            {Array.from({ length: 24 }, (_, hour) => (
                                                <span key={hour} className="sh-timeline__hour-label">
                                                    {shortHourLabel(hour)}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Summary */}
                                    {summary ? (
                                        <p className="sh-summary">{summary}</p>
                                    ) : null}

                                    {/* Action buttons */}
                                    {hasChanges ? (
                                        <div className="sh-actions">
                                            <button type="button" className="ap-btn ap-btn--secondary" onClick={handleReset} disabled={saving}>
                                                Reset
                                            </button>
                                            <button type="button" className="ap-btn ap-btn--primary" onClick={handleSave} disabled={saving}>
                                                {saving ? 'Saving...' : 'Save Changes'}
                                            </button>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ShiftSettingsPage;
